/**
 * Portal sign-in client (#27). Sign-in is a full-page redirect through the
 * proxy to Andian's broker. The session itself is an HttpOnly cookie the
 * proxy sets — this module never sees the token. `authRequired` comes from the
 * proxy's AUTH_MODE, so the gate switches on with the server, not a rebuild.
 */
import { useQuery } from "@tanstack/react-query";

export interface PortalUser {
  email: string;
  name: string;
  roles: string[];
}

export interface SessionInfo {
  authRequired: boolean;
  user: PortalUser | null;
}

export const SESSION_QUERY_KEY = ["auth", "me"] as const;

export function useSession() {
  return useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: async (): Promise<SessionInfo> => {
      const res = await fetch("/api/auth/me", { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return (await res.json()) as SessionInfo;
    },
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/** Same-origin paths only — never let `next` become an open redirect. */
export function safeNext(next: string | null | undefined): string {
  if (!next || !next.startsWith("/") || next.startsWith("//") || next.includes("\\")) {
    return "/";
  }
  if (next.startsWith("/signin") || next.startsWith("/auth/")) return "/";
  return next;
}

export function currentPath(): string {
  return window.location.pathname + window.location.search;
}

/** Where the proxy starts the broker sign-in flow (full-page navigation). */
export function loginUrl(next: string | null | undefined): string {
  return `/api/auth/login?next=${encodeURIComponent(safeNext(next))}`;
}

export function redirectToSignIn(): void {
  if (window.location.pathname.startsWith("/signin")) return;
  window.location.assign(loginUrl(currentPath()));
}

/** Any API call that comes back 401 sends the user to sign in. */
export function checkUnauthorized(res: Response): void {
  if (res.status === 401) redirectToSignIn();
}

/** Ends the portal session, then the broker session (its end-session page). */
export async function signOut(): Promise<void> {
  let target = "/";
  try {
    const res = await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
    if (res.ok) {
      const body = (await res.json()) as { end_session_url?: string };
      if (body.end_session_url) target = body.end_session_url;
    }
  } catch {
    /* fall back to "/" */
  }
  window.location.assign(target);
}
